import { Stack, Typography } from "@mui/material";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFetchAllEmailAssignees, useUpdateEmailAccount } from "controller";
import { MailBoxSection } from "./mail-server/MailBoxSection";
import { ServerCards } from "./mail-server/components/ServerCards";
import CustomMailServerComponent from "./mail-server/components/custom-server";
import { SelectAssignees } from "./mail-server/components";
import toast from "react-hot-toast";
import { TestEmailConnection } from "./TestEmailConnection";

export const OwnMailServer = forwardRef(
  (
    {
      emailConfigData,
      isTestEmailConnectionSuccess,
      setIsTestEmailConnectionSuccess,
      isOnboarding,
      selectedEmail,
      setSelectedmail,
    }: {
      emailConfigData?: any;
      isTestEmailConnectionSuccess: boolean;
      setIsTestEmailConnectionSuccess: (value: boolean) => void;
      isOnboarding?: boolean;
      selectedEmail: string;
      setSelectedmail: (value: string) => void;
    },
    ref,
  ) => {
    const detailsRef = useRef<any>(null);
    const customMailServerRef = useRef<any>(null);
    const [isAccountVerified, setIsAccountVerified] = useState(false);
    const [resourceSelectionState, setResourceSelectionState] = useState(false);
    const [selectedResources, setSelectedResources] = useState<Array<string>>(
      [],
    );
    const { mutateAsync: updateEmailAccount } = useUpdateEmailAccount();

    const { data: currentSelectedAssignees } = useFetchAllEmailAssignees(
      emailConfigData?.mapping_id,
    );

    useEffect(() => {
      if (emailConfigData) {
        setIsAccountVerified(true);

        setSelectedmail(
          emailConfigData?.provider === "custom_imap"
            ? "custom"
            : emailConfigData?.provider,
        );
      }
    }, [emailConfigData]);

    useEffect(() => {
      Array.isArray(currentSelectedAssignees)
        ? setSelectedResources(currentSelectedAssignees)
        : setSelectedResources([]);
    }, [currentSelectedAssignees]);

    const selectAssigneeFuntion = () => {
      setResourceSelectionState(!resourceSelectionState);
    };

    const submit: any = async () => {
      if (selectedEmail === "custom") {
        const customData = await customMailServerRef.current?.submit();
        if (customData) {
          return { ...customData };
        }
      } else if (detailsRef.current) {
        const result = await detailsRef.current.submit();

        if (result) {
          return {
            assignees: selectedResources.map(
              (assignee: any) => assignee?.mapping_id,
            ),
            ...result,
            display_from: result?.display_from,
          };
        }
      }
    };

    const onUpdateEmailAccount = async () => {
      try {
        const result = await updateEmailAccount({
          mappingId: emailConfigData?.mapping_id,
        });
        if (result) {
          window.open(result, "_self");
        }
      } catch (e) {
        toast.error(
          "Unable to complete action at the moment. Please try again later",
        );
      }
    };

    const onRemoveAssignee = (mappingId: any) => {
      setSelectedResources((prev: any) =>
        prev.filter((a: any) => a.mapping_id !== mappingId),
      );
    };

    // Expose the function to the parent component
    useImperativeHandle(ref, () => ({
      submit,
    }));

    const getServerDetailsForm = useMemo(() => {
      if (isAccountVerified && selectedEmail !== "custom") {
        if (isTestEmailConnectionSuccess) {
          return (
            <MailBoxSection
              ref={detailsRef}
              selectedResources={selectedResources}
              onSelectAssigneeClicked={selectAssigneeFuntion}
              onRemoveAssigneeClicked={onRemoveAssignee}
              board={emailConfigData?.board}
              customDisplayName={emailConfigData?.custom_display_name}
              displayFrom={emailConfigData?.display_from}
            />
          );
        } else {
          return (
            <TestEmailConnection
              mappingId={emailConfigData?.mapping_id}
              setIsTestEmailConnectionSuccess={setIsTestEmailConnectionSuccess}
            />
          );
        }
      } else {
        return <></>;
      }
    }, [isAccountVerified, selectedEmail, isTestEmailConnectionSuccess]);

    return (
      <Stack
        direction={"row"}
        sx={{
          height: "100%",
          padding: "8px",
          gap: "8px",
          overflow: "hidden",
          width: "100%",
        }}
      >
        <Stack
          p="8px"
          gap="8px"
          sx={{
            flex: 1,
            width: "624px",
            maxHeight: "100%",
            bgcolor: "#F6F6F5",
            borderRadius: "8px",
            overflowY: "scroll",
          }}
        >
          <Stack
            sx={{
              bgcolor: "#FFFF",
              p: "24px",
              gap: "24px",
              borderRadius: "8px",
            }}
          >
            <ServerCards
              onClick={setSelectedmail}
              value={selectedEmail}
              email={emailConfigData?.support_email || ""}
              isDisabled={emailConfigData}
              isAuthVerified={isAccountVerified}
              onChangeClick={onUpdateEmailAccount}
              isOnboarding={isOnboarding}
              ref={customMailServerRef}
              emailConfigData={emailConfigData}
            />
          </Stack>
          <Stack gap="24px" sx={{ width: "100%" }}>
            {getServerDetailsForm}
          </Stack>
        </Stack>
        {resourceSelectionState && (
          <Stack
            p="8px"
            gap="8px"
            sx={{
              flex: 1,
              maxWidth: "624px",
              height: "100%",
              bgcolor: "#F6F6F5",
              borderRadius: "8px",
              transition: "all 0.3s ease-in-out",
            }}
          >
            <SelectAssignees
              selectedAssignees={selectedResources}
              setSelectedAssignees={(value) => {
                setSelectedResources(value);
              }}
            />
          </Stack>
        )}
      </Stack>
    );
  },
);
